import { identity, max, memoize } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import tinycolor2 from 'tinycolor2';
import { useEvent } from '../../../../data/hooks/eventHooks';
import { useI18n } from '../../../../data/hooks/i18nHooks';
import { useFilteredSchedule } from '../../../../data/hooks/schedule/useFilteredSchedule';
import { useScheduleFilter } from '../../../../data/hooks/schedule/useScheduleFilter';
import ButtonLink from '../../../elements/ButtonLink';
import CollapsableCategory from '../../../elements/CollapsableCategory/CollapsableCategory';
import Scrollable from '../../../elements/Scrollable';
import SelectableFilterItem from '../../../elements/SelectableFilterItem/SelectableFilterItem';
import TopBarEvent from '../../../elements/TopBar/TopBarEvent';
import Screen from '../../Screen';
import { useDefaultScheduleTab } from '../EventScheduleDefaultRoute/defaultScheduleTabTools';

const EventScheduleFilterScreen = (props) => {
    const { eventId, expandCategory } = props,
        event = useEvent(eventId),
        i18n = useI18n(),
        [expandedCats, setExpandedCats] = useState([expandCategory].filter(identity)),
        [filter, hasFilter, isRecommendedFilter, categories, recommendedFilter] = useScheduleFilter(eventId),
        filtered = useFilteredSchedule(eventId),
        [originalFilter, setOriginalFilter] = useState(),
        defaultTab = useDefaultScheduleTab(eventId);

    const handleCloseScreen = useCallback(() => {
        if (hasFilter) {
            app.history.replace('..');
        } else {
            app.history.push(`../../${defaultTab}`);
        }
    }, [hasFilter, defaultTab]);

    useEffect(() => {
        setOriginalFilter(filter);
    }, [eventId]);

    useEffect(() => {
        if (!expandedCats.length) {
            let cats = Object.keys(filter.categories || {})
                .map((cat) => (filter.categories[cat].length ? cat : null))
                .filter(identity);

            if (!cats.length && categories.length) {
                cats = [categories[0].name];
            }

            setExpandedCats(cats);
        }
    }, []);

    const handleCancel = useCallback(
            (e) => {
                e.stopPropagation();
                console.log('DBG', originalFilter);
                app.store.dispatch(app.actions.program.setFilter(eventId, originalFilter));
                handleCloseScreen();
            },
            [eventId, handleCloseScreen, originalFilter]
        ),
        handleApplyFilter = useCallback(() => handleCloseScreen(), [handleCloseScreen]),
        handleApplyRecommendedFilter = useCallback(() => {
            if (recommendedFilter && !isRecommendedFilter) {
                app.store.dispatch(app.actions.program.applyRecommendedFilter(eventId));
                setExpandedCats(recommendedFilter.map((cat) => cat.name));
            }
        }, [eventId, recommendedFilter, isRecommendedFilter]),
        handleCollapseChange = useCallback(
            (catName, expanded) => {
                if (expanded) {
                    setExpandedCats([...expandedCats, catName]);
                } else {
                    setExpandedCats(expandedCats.filter((cat) => cat != catName));
                }
            },
            [expandedCats]
        ),
        handleSelectionChange = useCallback(
            (cat, option, checked) => {
                if (!checked) {
                    app.store.dispatch(app.actions.program.addFilterCategoryOption(eventId, cat.name, option.name));
                } else {
                    app.store.dispatch(app.actions.program.removeFilterCategoryOption(eventId, cat.name, option.name));
                }
            },
            [eventId]
        );

    const getMaxOptionsCount = useCallback(memoize((options) => max(options.map((o) => o.count ?? 0)) ?? 0));

    const header = (
            <>
                <TopBarEvent event={event} onReturnClick={handleCancel} />
                <hr />
            </>
        ),
        footer = (
            <div>
                <hr />
                <div className="content">
                    <ButtonLink style={event._local.styles.button} onClick={handleApplyFilter}>
                        {i18n['button.filter-results']}
                        {<span>&nbsp;({filtered.length})</span>}
                    </ButtonLink>
                    {recommendedFilter && !isRecommendedFilter ? (
                        <ButtonLink
                            onClick={handleApplyRecommendedFilter}
                            style={{
                                fontWeight: 'bold',
                                backgroundColor: 'white',
                                border: 0,
                            }}>
                            {i18n['button.filter-recommended']}
                        </ButtonLink>
                    ) : null}
                </div>
            </div>
        );

    return (
        <Screen header={header} footer={footer}>
            <Scrollable>
                {categories.map((category) => {
                    let expanded = expandedCats.includes(category.name),
                        title = category.name;

                    return (
                        <CollapsableCategory
                            title={title}
                            flagColor={event.settings.primary_color}
                            expanded={expanded}
                            disabled={!category.options.length}
                            key={category.name}
                            onChange={() => handleCollapseChange(category.name, !expanded)}>
                            <ul className="list">
                                {category.options.map((option) => {
                                    let isChecked = _(filter).chain().get(`categories[${category.name}]`, []).includes(option.name).value(),
                                        lightColor = tinycolor2(option.color).lighten(30).toString(),
                                        maxCount = getMaxOptionsCount(category.options);

                                    return (
                                        <li key={option.name}>
                                            <SelectableFilterItem
                                                checked={isChecked}
                                                color={option.color}
                                                count={option.count}
                                                percentage={maxCount ? option.count / maxCount : 0}
                                                onChange={() => handleSelectionChange(category, option, isChecked)}>
                                                {option.name}
                                            </SelectableFilterItem>
                                        </li>
                                    );
                                })}
                            </ul>
                        </CollapsableCategory>
                    );
                })}
            </Scrollable>
        </Screen>
    );
};

export default (props) => <EventScheduleFilterScreen {...props.match.params} />;
